import {createClient, fetchExchange} from "urql";
import {print, stripIgnoredCharacters, type DocumentNode} from "graphql";

import {getApiPath} from "../utils/api-path";
import {getAuthorizationHeaders, getOperationKind} from "../utils/graphql-client";

type GraphQLVariables = Record<string, unknown> | undefined;
type GraphQLData = Record<string, unknown>;
type GraphQLDocument = DocumentNode | string;

let client: ReturnType<typeof createClient> | undefined;
const minifiedQueryCache = new WeakMap<DocumentNode, string>();

function minifyQueryText(query: string): string {
  try {
    return stripIgnoredCharacters(query);
  } catch {
    return query;
  }
}

function toMinifiedQuery(document: GraphQLDocument): string {
  if (typeof document === "string") {
    return minifyQueryText(document);
  }

  if (minifiedQueryCache.has(document)) {
    return minifiedQueryCache.get(document)!;
  }

  const printed = print(document);
  const minified = minifyQueryText(printed);

  minifiedQueryCache.set(document, minified);

  return minified;
}

function getOperationKindFromDocument(document: GraphQLDocument): "query" | "mutation" {
  if (typeof document === "string") {
    const normalized = document.trimStart();
    return normalized.startsWith("mutation") ? "mutation" : "query";
  }

  return getOperationKind(document);
}

function minifyRequestPayload(init?: RequestInit): RequestInit | undefined {
  if (!init) {
    return init;
  }

  const method = `${init.method || "GET"}`.toUpperCase();

  if (typeof init.body !== "string") {
    return init;
  }

  if (method !== "POST") {
    return init;
  }

  try {
    const parsed = JSON.parse(init.body);

    if (!parsed || typeof parsed.query !== "string") {
      return init;
    }

    parsed.query = minifyQueryText(parsed.query);

    return {
      ...init,
      body: JSON.stringify(parsed),
    };
  } catch {
    return init;
  }
}

function minifyGraphQLRequest(input: RequestInfo | URL, init?: RequestInit): [RequestInfo | URL, RequestInit | undefined] {
  const requestInit = minifyRequestPayload(init);

  if (typeof input === "string") {
    try {
      const url = new URL(input, window.location.origin);
      const rawQuery = url.searchParams.get("query");

      if (rawQuery) {
        url.searchParams.set("query", minifyQueryText(rawQuery));
      }

      return [url.toString(), requestInit];
    } catch {
      return [input, requestInit];
    }
  }

  if (input instanceof URL) {
    const rawQuery = input.searchParams.get("query");

    if (rawQuery) {
      input.searchParams.set("query", minifyQueryText(rawQuery));
    }

    return [input, requestInit];
  }

  return [input, requestInit];
}

async function minifyingFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const [nextInput, nextInit] = minifyGraphQLRequest(input, init);
  return fetch(nextInput, nextInit);
}

function getClient() {
  if (!client) {
    client = createClient({
      url: getApiPath(),
      exchanges: [fetchExchange],
      fetch: minifyingFetch,
      fetchOptions: () => {
        const headers = getAuthorizationHeaders();

        return {
          credentials: "include",
          headers,
        };
      },
    });
  }

  return client;
}

export async function requestGraphQL(document: GraphQLDocument, variables?: GraphQLVariables): Promise<GraphQLData> {
  const operationKind = getOperationKindFromDocument(document);
  const query = toMinifiedQuery(document);
  const activeClient = getClient();
  const headers = getAuthorizationHeaders();
  try {
    const result = operationKind === "mutation"
      ? await activeClient.mutation(query, variables, {
        fetchOptions: {
          method: "POST",
          credentials: "include",
          headers,
        },
      }).toPromise()
      : await activeClient.query(query, variables).toPromise();

    if (result.error) {
      throw result.error;
    }

    return (result.data || {}) as GraphQLData;
  } catch (error) {
    console.error("GraphQL request failed:", error, document, variables);
    throw error;
  }
}
